import React from 'react';
import { Alert, Box, Button, Divider, Grid, Skeleton } from '@mui/material';
import KeyValueList from '../ui/KeyValueList';
import Section from '../ui/Section';
import UsageMeter from '../ui/UsageMeter';
import { formatBytes } from '../../utils/format';
import { formatLimit, formatSizeLimit, nextPlanUp, projectsHint, storageHint } from './billingModel';
import type { SubscriptionPlan, UsageStats } from './billingTypes';

interface UsageSectionProps {
  usage: UsageStats | null | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /** The plan whose limits apply (the API's usage figures already use it). */
  plan?: SubscriptionPlan;
  /** All plans, for the "upgrade to Pro for 10 GB" hints. */
  plans: SubscriptionPlan[];
  planId: string;
}

/**
 * Storage and active projects as meters; the per-file and per-project limits
 * as facts. Sizes are binary (1 GB = 1024^3 bytes), the same base the API
 * enforces.
 */
const UsageSection: React.FC<UsageSectionProps> = ({ usage, loading, error, onRetry, plan, plans, planId }) => {
  let body: React.ReactNode;
  if (loading && !usage) {
    body = (
      <Box role="status" aria-label="Loading usage">
        <Grid container spacing={3}>
          {[0, 1].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton width="40%" />
              <Skeleton variant="rounded" height={6} sx={{ mt: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  } else if (error || !usage) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        We couldn&apos;t load your usage. Your plan and billing aren&apos;t affected.
      </Alert>
    );
  } else {
    const storageUpgrade = nextPlanUp(plans, planId, (limits) => limits.storage);
    const projectsUpgrade = nextPlanUp(plans, planId, (limits) => limits.projects);
    const changeRounds = plan?.limits.revisions ?? usage.revisions?.limit;
    body = (
      <>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <UsageMeter
              label="Storage"
              used={usage.storage.used}
              limit={usage.storage.limit}
              format={(value) => formatBytes(value)}
              nearLimitHint={storageHint(usage.storage.used, usage.storage.limit, storageUpgrade)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <UsageMeter
              label="Active projects"
              used={usage.projects.used}
              limit={usage.projects.limit}
              nearLimitHint={projectsHint(usage.projects.used, usage.projects.limit, projectsUpgrade)}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 2.5 }} />
        <KeyValueList
          items={[
            { label: 'Largest file', value: formatSizeLimit(plan?.limits.uploadSize) },
            { label: 'Change rounds per project', value: formatLimit(changeRounds) },
          ]}
        />
      </>
    );
  }

  return (
    <Section title="Usage" description="Approved, delivered and cancelled projects don't count toward active projects.">
      {body}
    </Section>
  );
};

export default UsageSection;
